import { useEffect, useRef } from 'react';

/**
 * ParticleBackground
 * Canvas-based floating particle constellation. Fills whichever relative/
 * absolute container it sits in. Adapts to light/dark mode automatically.
 *
 * Props:
 *   particleCount  - number of particles (default 65)
 *   maxDist        - px distance at which particles connect (default 130)
 *   speed          - max velocity per axis (default 0.35)
 *   className      - extra classes for the <canvas> element
 */
export default function ParticleBackground({
  particleCount = 65,
  maxDist = 130,
  speed = 0.35,
  className = '',
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let particles = [];

    const isDark = () =>
      document.documentElement.classList.contains('dark');

    // Resize canvas to match CSS dimensions (DPR-aware)
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      // Re-scatter particles when container resizes
      if (particles.length) {
        particles.forEach(p => {
          p.x = Math.min(p.x, rect.width);
          p.y = Math.min(p.y, rect.height);
        });
      }
    };

    const cssW = () => canvas.getBoundingClientRect().width;
    const cssH = () => canvas.getBoundingClientRect().height;

    const initParticles = () => {
      const w = cssW();
      const h = cssH();
      particles = Array.from({ length: particleCount }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * speed * 2,
        vy: (Math.random() - 0.5) * speed * 2,
        r: Math.random() * 1.2 + 0.6,
      }));
    };

    const draw = () => {
      const w = cssW();
      const h = cssH();
      ctx.clearRect(0, 0, w, h);

      const dark = isDark();
      // Dot color: slate-400 dark / slate-600 light (darker = more visible on white bg)
      const dotBase = dark ? '148,163,184' : '71,85,105';
      // Line color matches dots
      const lineBase = dark ? '148,163,184' : '71,85,105';

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Move
        p.x += p.vx;
        p.y += p.vy;

        // Soft bounce off walls
        if (p.x <= 0 || p.x >= w) p.vx *= -1;
        if (p.y <= 0 || p.y >= h) p.vy *= -1;

        // Clamp
        p.x = Math.max(0, Math.min(w, p.x));
        p.y = Math.max(0, Math.min(h, p.y));

        // Draw dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${dotBase},${dark ? 0.6 : 0.75})`;
        ctx.fill();

        // Draw connections to subsequent particles only (avoid double-draw)
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const distSq = dx * dx + dy * dy;
          const maxDistSq = maxDist * maxDist;
          if (distSq < maxDistSq) {
            const alpha = (1 - Math.sqrt(distSq) / maxDist) * (dark ? 0.30 : 0.45);
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(${lineBase},${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };

    resize();
    initParticles();
    draw();

    const ro = new ResizeObserver(() => {
      resize();
    });
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, [particleCount, maxDist, speed]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`absolute inset-0 w-full h-full pointer-events-none select-none ${className}`}
    />
  );
}
